import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Descriptions, Drawer, Space, Spin, Tag, Typography, message } from 'antd';
import {
  formatDate,
  formatTime,
  supabase,
  routeDriveSourceI18nKey,
  routeDriveSourceTagColor,
  formatRouteDriveKm,
  type Trip,
} from '@ubus/shared';
import StatusTag from './StatusTag';

const { Text, Title } = Typography;

type PeekTrip = Trip & {
  vehicles?: { fleet_number?: string; model?: string } | null;
  drivers?: { name?: string } | null;
  organizations?: { name?: string } | null;
};

export interface OverviewTripPeekDrawerProps {
  tripId: string | null;
  open: boolean;
  onClose: () => void;
  isOwner: boolean;
  onOpenInTrips: (trip: PeekTrip) => void;
  onOpenOwnerSchedule: (date: string, tripId: string) => void;
}

export function OverviewTripPeekDrawer({
  tripId,
  open,
  onClose,
  isOwner,
  onOpenInTrips,
  onOpenOwnerSchedule,
}: OverviewTripPeekDrawerProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [trip, setTrip] = useState<PeekTrip | null>(null);

  useEffect(() => {
    if (!open || !tripId) {
      setTrip(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from('trips')
        .select('*, vehicles(fleet_number, model), drivers(name), organizations(name)')
        .eq('id', tripId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        message.error(t('scheduleOverview.peekLoadFailed'));
        setTrip(null);
        onClose();
      } else {
        setTrip(data as PeekTrip);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tripId, onClose, t]);

  const vehicleLabel = trip?.vehicles
    ? `${trip.vehicles.fleet_number ?? '—'}${trip.vehicles.model ? ` (${trip.vehicles.model})` : ''}`
    : '—';

  return (
    <Drawer
      title={t('scheduleOverview.peekTitle')}
      open={open}
      onClose={onClose}
      width={420}
      destroyOnClose
      footer={
        trip ? (
          <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>{t('common.close')}</Button>
            {isOwner ? (
              <Button
                type="primary"
                onClick={() => onOpenOwnerSchedule(trip.date, trip.id)}
              >
                {t('scheduleOverview.peekOpenOwner')}
              </Button>
            ) : (
              <Button type="primary" onClick={() => onOpenInTrips(trip)}>
                {t('scheduleOverview.peekOpenTrips')}
              </Button>
            )}
          </Space>
        ) : null
      }
    >
      {loading || !trip ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {trip.organizations?.name || trip.pickup_address?.slice(0, 40) || trip.id.slice(0, 8)}
            </Title>
            <Space wrap style={{ marginTop: 8 }}>
              <StatusTag status={trip.status} />
              {trip.source ? <Tag>{trip.source}</Tag> : null}
            </Space>
          </div>

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('scheduleOverview.peekDate')}>
              {formatDate(trip.date)}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.fields.pickupTime')}>
              {trip.pickup_time ? formatTime(trip.pickup_time) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.fields.estimatedDropoff')}>
              {trip.estimated_dropoff_time
                ? formatTime(trip.estimated_dropoff_time)
                : t('trips.estimatedDropoffPending')}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.fields.routeDrive')}>
              {typeof trip.route_drive_minutes === 'number' &&
              routeDriveSourceI18nKey(trip.route_drive_source) ? (
                <Tag color={routeDriveSourceTagColor(trip.route_drive_source)}>
                  {t(`trips.${routeDriveSourceI18nKey(trip.route_drive_source)}`, {
                    minutes: trip.route_drive_minutes,
                    kmSuffix: formatRouteDriveKm(trip.route_drive_distance_meters)
                      ? t('trips.routeDriveKmSuffix', {
                          km: formatRouteDriveKm(trip.route_drive_distance_meters),
                        })
                      : '',
                  })}
                </Tag>
              ) : (
                t('trips.routeDriveUnavailable')
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.fields.endTimeOccupancy')}>
              {trip.end_time ? formatTime(trip.end_time) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.fields.vehicle')}>{vehicleLabel}</Descriptions.Item>
            <Descriptions.Item label={t('trips.fields.driver')}>
              {trip.drivers?.name || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.fields.passengers')}>
              {trip.passenger_count ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.fields.pickup')}>
              {trip.pickup_address}
            </Descriptions.Item>
            <Descriptions.Item label={t('trips.fields.dropoff')}>
              {trip.dropoff_address}
            </Descriptions.Item>
          </Descriptions>

          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('scheduleOverview.peekHint')}
          </Text>
        </Space>
      )}
    </Drawer>
  );
}

export default OverviewTripPeekDrawer;
